import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CalendarDays, House, LayoutGrid, LogIn, Map, Phone, Siren, Sprout, Building2, Star, Settings, ShieldCheck, Info, Ticket, WifiOff, TriangleAlert, User, Briefcase, Inbox, FileText, ScrollText, Receipt, MessageSquareWarning, BookOpen, MessageSquareHeart, Search, BookOpenText, Building, Lightbulb, PackageSearch, Recycle, ShoppingBasket, Mountain, Scale, FolderOpen } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { LguLockup, PoweredBy } from '../components/Brand';
import { useT } from '../i18n';
import { get } from '../lib/api';
import { useMe } from '../lib/auth';
import { useSettings } from '../lib/settings';
import { useEventSource } from '../lib/sse';
import { useOnline } from '../offline/useOutbox';

function UnreadDot() {
  const { me } = useMe();
  const q = useQuery({ queryKey: ['notifications', 'unread'], queryFn: () => get<{ count: number }>('/me/notifications/unread-count'), enabled: !!me, refetchInterval: 60_000 });
  const n = q.data?.count ?? 0;
  return n > 0 ? <span className="dot" aria-hidden="true">{n > 9 ? '9+' : n}</span> : null;
}

export function AppShell() {
  const t = useT();
  const s = useSettings();
  const { me } = useMe();
  const online = useOnline();
  const loc = useLocation();
  const qc = useQueryClient();
  const mainRef = useRef<HTMLElement>(null);

  // Emergency mode and settings changes arrive live.
  useEventSource(online ? '/api/stream/public' : null, {
    'lgu.updated': () => qc.invalidateQueries({ queryKey: ['lgu'] }),
  });

  // Move focus to the page on navigation (screen readers announce the new page).
  useEffect(() => {
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [loc.pathname]);

  const side = (to: string, label: string, Icon: typeof House, cls = '') => (
    <NavLink to={to} className={`side-link ${cls}`} end={to === '/'}>
      <Icon size={20} aria-hidden="true" /> {label}
    </NavLink>
  );

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="app-header">
        <Link to="/" className="lockup" aria-label={`${s.displayName} home`}>
          <LguLockup compact />
        </Link>
        {/* Named for screen readers: the visible label is hidden on the narrowest phones. */}
        <Link to="/emergency" className="btn btn-emergency header-emergency" aria-label={t('nav.emergency')}>
          <Siren size={18} aria-hidden="true" />
          <span className="label">{t('common.emergency')}</span>
        </Link>
        <Link to="/search" className="icon-btn" aria-label={t('nav.search')}>
          <Search size={22} />
        </Link>
        <Link to="/notifications" className="icon-btn" aria-label={t('nav.notifications')}>
          <Bell size={22} />
          <UnreadDot />
        </Link>
        {me ? (
          <Link to="/settings" className="icon-btn" aria-label={t('nav.account')}>
            <User size={22} />
          </Link>
        ) : (
          <Link to={`/signin?next=${encodeURIComponent(loc.pathname)}`} className="icon-btn" aria-label={t('common.signIn')}>
            <LogIn size={22} />
          </Link>
        )}
      </header>
      {!online && (
        <div className="offline-strip" role="status">
          <WifiOff size={18} aria-hidden="true" /> {t('common.offline')}
        </div>
      )}
      {s.emergency.on && loc.pathname !== '/emergency' && (
        <Link to="/map" className="emergency-strip" role="alert">
          <TriangleAlert size={20} aria-hidden="true" />
          <span className="grow">{s.emergency.message || t('home.emergencyModeOn')}</span>
          <span className="nowrap small">{t('home.nearestSite')} →</span>
        </Link>
      )}
      <div className="app-body">
        <nav className="sidebar" aria-label="Main">
          <div className="side-group">
            <h2>{t('nav.groupEssentials')}</h2>
            {side('/', t('nav.home'), House)}
            {side('/calendar', t('nav.calendar'), CalendarDays)}
            {side('/offices', t('nav.offices'), Building2)}
            {side('/mine', t('nav.mine'), Ticket)}
            {side('/requests', t('nav.myRequests'), Inbox)}
            {side('/notifications', t('nav.notifications'), Bell)}
          </div>
          <div className="side-group">
            <h2>{t('nav.groupServices')}</h2>
            {side('/services', t('nav.services'), FileText)}
            {side('/civil-registry', t('nav.civilRegistry'), ScrollText)}
            {side('/taxes', t('nav.taxes'), Receipt)}
            {side('/report', t('nav.reportConcern'), MessageSquareWarning)}
            {side('/charter', t('nav.charter'), BookOpen)}
            {side('/feedback', t('nav.feedback'), MessageSquareHeart)}
          </div>
          <div className="side-group">
            <h2>{t('nav.groupCommunity')}</h2>
            {side('/guides', t('nav.guides'), BookOpenText)}
            {side('/facilities', t('nav.facilities'), Building)}
            {side('/proposals', t('nav.proposals'), Lightbulb)}
            {side('/lost-found', t('nav.lostFound'), PackageSearch)}
            {side('/recycling', t('nav.recycling'), Recycle)}
          </div>
          <div className="side-group">
            <h2>{t('nav.groupExplore')}</h2>
            {side('/market-prices', t('nav.marketPrices'), ShoppingBasket)}
            {side('/tourism', t('nav.tourism'), Mountain)}
            {side('/transparency', t('nav.transparency'), Scale)}
            {side('/documents', t('nav.documents'), FolderOpen)}
          </div>
          <div className="side-group">
            <h2>{t('nav.groupSafety')}</h2>
            {side('/emergency', t('nav.emergency'), Siren, 'emergency')}
            {side('/map', t('nav.map'), Map)}
            {side('/hotlines', t('nav.hotlines'), Phone)}
            {side('/crop-damage', t('nav.cropDamage'), Sprout)}
          </div>
          <div className="side-group">
            <h2>{t('nav.groupAccount')}</h2>
            {side('/following', t('nav.following'), Star)}
            {side('/settings', t('nav.settings'), Settings)}
            {side('/privacy', t('nav.privacy'), ShieldCheck)}
            {side('/about', t('nav.about'), Info)}
            {me?.accountType === 'staff' && side('/staff', t('nav.staffWorkspace'), Briefcase)}
          </div>
          <div style={{ marginTop: 'auto', padding: '12px' }}>
            <PoweredBy />
          </div>
        </nav>
        <div className="grow" style={{ display: 'flex', flexDirection: 'column' }}>
          <main id="main" className="app-main" tabIndex={-1} ref={mainRef}>
            <Outlet />
            <footer className="app-footer">
              <PoweredBy />
              <nav aria-label="Footer">
                <Link to="/about">{t('nav.about')}</Link>
                <Link to="/privacy">{t('nav.privacy')}</Link>
                <Link to="/hotlines">{t('nav.hotlines')}</Link>
              </nav>
              <span>{s.lguName}, {s.province}</span>
            </footer>
          </main>
        </div>
      </div>
      <nav className="bottom-nav" aria-label="Main">
        {[
          ['/', t('nav.home'), House],
          ['/calendar', t('nav.calendar'), CalendarDays],
          ['/map', t('nav.map'), Map],
          ['/mine', t('nav.mine'), Ticket],
          ['/more', t('nav.more'), LayoutGrid],
        ].map(([to, label, Icon]) => {
          const I = Icon as typeof House;
          return (
            <NavLink key={to as string} to={to as string} end={to === '/'}>
              <span className="ic">
                <I size={22} aria-hidden="true" />
              </span>
              {label as string}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
